/**
 * TOML → YAML Tool
 *
 * Converts TOML into YAML.
 */

import { parse as parseToml } from 'smol-toml';
import { stringify as stringifyYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type IndentSize = '2' | '4';

export const TomlToYamlTool: ITransformerTool = {
  id: 'toml-to-yaml',
  name: 'TOML → YAML',
  description: 'Convert TOML to YAML',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['toml', 'yaml', 'convert', 'transform', 'config'],

  seo: {
    slug: 'toml-to-yaml-converter',
    pageTitle: 'TOML to YAML Converter - Free Online Tool | SWE DevTools',
    metaDescription: 'Convert TOML configuration files to YAML instantly. Supports nested tables, arrays, and all TOML data types. Fast, private, runs in your browser.',
    keywords: ['toml to yaml', 'toml converter', 'convert toml to yaml online', 'toml yaml tool'],
    relatedTools: ['yaml-to-toml', 'toml-to-json', 'toml-validate'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.YAML,

  attributes: [
    {
      name: 'indentSize',
      type: 'select',
      label: 'Indent Size',
      defaultValue: '2',
      options: ['2', '4'],
      description: 'YAML indentation',
    },
    {
      name: 'sortKeys',
      type: 'boolean',
      label: 'Sort Keys',
      defaultValue: false,
      description: 'Sort object keys alphabetically',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    let parsed: unknown;

    try {
      parsed = parseToml(input);
    } catch (e) {
      const error = e as Error;
      throw ToolError.validation(`Invalid TOML: ${error.message}`, {
        suggestion: 'Check key-value syntax and ensure tables use [table] or [table.subtable] format',
      });
    }

    if (config.sortKeys) {
      parsed = sortObjectKeys(parsed);
    }

    const indentSize = (config.indentSize as IndentSize) || '2';
    const indent = parseInt(indentSize, 10);

    return stringifyYaml(parsed, {
      indent,
      lineWidth: 0,
    }).trimEnd();
  },
};

function sortObjectKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }

  if (obj !== null && typeof obj === 'object') {
    const sorted: Record<string, unknown> = {};
    const keys = Object.keys(obj as Record<string, unknown>).sort();
    for (const key of keys) {
      sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
    }
    return sorted;
  }

  return obj;
}
